import type { ISODateString, PeriodMonth } from '../types/timesheet.types';
import { toISODateString, toPeriodMonth } from '../types/timesheet.types';

/** Parse YYYY-MM-DD without timezone conversion (treats as local date). */
export function parseISODate(iso: ISODateString): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Format a Date to YYYY-MM-DD. */
export function formatISODate(date: Date): ISODateString {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return toISODateString(`${y}-${m}-${d}`);
}

/** Derive PeriodMonth from a date or ISO string. */
export function getPeriodMonth(date: Date | ISODateString): PeriodMonth {
  const d = typeof date === 'string' ? parseISODate(date) : date;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return toPeriodMonth(`${y}-${m}`);
}

export function parsePeriodMonth(pm: PeriodMonth): { year: number; month: number } {
  const [year, month] = pm.split('-').map(Number);
  return { year, month };
}

/** First and last day of a calendar month (local time). */
export function getMonthBounds(pm: PeriodMonth): { start: Date; end: Date } {
  const { year, month } = parsePeriodMonth(pm);
  return {
    start: new Date(year, month - 1, 1),
    end: new Date(year, month, 0),
  };
}

export function isWeekend(date: Date): boolean {
  const d = date.getDay();
  return d === 0 || d === 6;
}

/** True if the date falls inside the given PeriodMonth. */
export function isDateInMonth(date: Date | ISODateString, pm: PeriodMonth): boolean {
  return getPeriodMonth(date) === pm;
}

/** Navigate by delta months. */
export function addMonths(pm: PeriodMonth, delta: number): PeriodMonth {
  const { year, month } = parsePeriodMonth(pm);
  const d = new Date(year, month - 1 + delta, 1);
  return getPeriodMonth(d);
}

/** Human-readable label, e.g. "April 2026". */
export function formatMonthLabel(pm: PeriodMonth): string {
  const { year, month } = parsePeriodMonth(pm);
  return new Date(year, month - 1, 1).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Returns the weeks that appear in the calendar view for a given month.
 * Each week starts on Monday. Weeks may include days from adjacent months
 * (straddling weeks) — those days get the 'oom' (out-of-month) treatment.
 */
export interface CalendarWeek {
  weekStart: ISODateString;
  days: ISODateString[];
}

export function getCalendarWeeksForMonth(pm: PeriodMonth): CalendarWeek[] {
  const { start, end } = getMonthBounds(pm);

  // Find the Monday on or before the 1st of the month
  const firstMonday = new Date(start);
  const dow = start.getDay(); // 0=Sun
  const offsetToMonday = dow === 0 ? -6 : 1 - dow;
  firstMonday.setDate(start.getDate() + offsetToMonday);

  const weeks: CalendarWeek[] = [];
  const cursor = new Date(firstMonday);

  while (cursor <= end) {
    const weekStart = formatISODate(cursor);
    const days: ISODateString[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(formatISODate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push({ weekStart, days });
  }

  return weeks;
}

/** All working days (Mon–Fri) in a calendar month. */
export function getWorkingDaysInMonth(pm: PeriodMonth): ISODateString[] {
  const { start, end } = getMonthBounds(pm);
  const days: ISODateString[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    if (!isWeekend(cursor)) {
      days.push(formatISODate(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

export function todayPeriodMonth(): PeriodMonth {
  return getPeriodMonth(new Date());
}

// ─── Week Utilities ───────────────────────────────────────────────────────────

/** Return the ISO date string for the Monday of the week containing `date`. */
export function getWeekStart(date: Date | ISODateString): ISODateString {
  const d = typeof date === 'string' ? parseISODate(date) : new Date(date.getTime());
  const dow = d.getDay(); // 0 = Sun
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return formatISODate(d);
}

/** Monday of the week containing today. */
export function getCurrentWeekStart(): ISODateString {
  return getWeekStart(new Date());
}

/** Navigate by ±delta weeks from a Monday `weekStart`. */
export function addWeeks(weekStart: ISODateString, delta: number): ISODateString {
  const d = parseISODate(weekStart);
  d.setDate(d.getDate() + delta * 7);
  return formatISODate(d);
}

/** Format "W/C 07 Apr 2026" for display in the topbar. */
export function formatWeekLabel(weekStart: ISODateString): string {
  const d = parseISODate(weekStart);
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleDateString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  return `W/C ${day} ${month} ${year}`;
}

/** The 7 calendar days [Mon…Sun] for the given week start (must be a Monday). */
export function getWeekDays(weekStart: ISODateString): ISODateString[] {
  const days: ISODateString[] = [];
  const d = parseISODate(weekStart);
  for (let i = 0; i < 7; i++) {
    days.push(formatISODate(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}
